import get from 'lodash/get';
import { TableCell, StatusCell } from 'ui/tableCell';
import { AccordionHeader, AccordionContent } from 'ui/accordion';
import { EVENT_TYPES, EVENT_TYPES_LOOKUP } from './constants';
import { TriggerEventDiv, StyledAccordion } from './SystemWebhooks.styled';
import { SystemWebhooksActionButton } from './components/SystemWebhooksActionButton';
import type { WebhookParamProps, WebhooksTableColumnData } from './types';

/**
 * **generateTriggerLabel**
 * Helper function for to generate a grammatically correct label given the event count
 * Used to generate COLUMNS constant
 * @param { Number } eventCount: Number of events
 * @returns { String } Accordion cell label to display in trigger events column
 */
const generateTriggerLabel = (eventCount: number) =>
  `${eventCount} system event${eventCount === 1 ? '' : 's'}`;

const ExcludeListUpdatesCell: WebhooksTableColumnData = {
  name: 'Exclude list updates',
  align: 'flex-start',
  value: 'id',
  RowCell: ({ row }) => <StatusCell showPositiveState={row.excludeListUpdates} />,
};

export const createColumns = (
  SystemWebhookAdvancedFiltering: boolean = false,
  openDeleteModal: (id: WebhookParamProps['id']) => void,
): WebhooksTableColumnData[] => [
  {
    name: 'ID',
    align: 'flex-start',
    value: 'id',
    RowCell: ({ row }) => <TableCell>{row.id}</TableCell>,
  },
  {
    name: 'Destination',
    align: 'flex-start',
    value: 'endpoint',
    RowCell: ({ row }) => <TableCell>{row.endpoint}</TableCell>,
  },
  {
    name: 'Status',
    align: 'flex-start',
    value: 'enabled',
    RowCell: ({ row }) => (
      <StatusCell
        showPositiveState={row.enabled}
        positiveLabel="Enabled"
        negativeLabel="Disabled"
      />
    ),
  },
  ...(SystemWebhookAdvancedFiltering ? [ExcludeListUpdatesCell] : []),
  {
    name: 'Trigger',
    align: 'flex-start',
    value: 'eventCount',
    sortable: false,
    RowCell: ({ row }) => {
      const { eventCount } = row;
      const label = generateTriggerLabel(eventCount);
      if (eventCount === 0) {
        return <TableCell>{label}</TableCell>;
      }
      return (
        <TableCell>
          <StyledAccordion>
            <AccordionHeader data-test="system-webhooks-table-accordion-button">
              Expand to see {eventCount} system events
            </AccordionHeader>
            <AccordionContent>
              {EVENT_TYPES.filter((eventType) => `${eventType}Events` in row).map(
                (filteredEventType) => {
                  const eventDetails =
                    EVENT_TYPES_LOOKUP[filteredEventType as keyof typeof EVENT_TYPES_LOOKUP];
                  const events = get(row, `${filteredEventType}Events`, []).map(
                    (event: string) =>
                      eventDetails.events[event as keyof typeof eventDetails.events],
                  );
                  return (
                    <TriggerEventDiv key={filteredEventType}>
                      <strong>{eventDetails.text}: </strong>
                      {events.join(', ')}
                    </TriggerEventDiv>
                  );
                },
              )}
            </AccordionContent>
          </StyledAccordion>
        </TableCell>
      );
    },
  },
  {
    name: '',
    align: 'flex-end',
    value: '',
    sortable: false,
    RowCell: ({ row }) => (
      <SystemWebhooksActionButton webhook={row} openDeleteModal={openDeleteModal} />
    ),
  },
];
